import React, { useState } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Play,
  RotateCcw,
  Clock,
  Sparkles,
  X,
  Layers,
  FileText
} from 'lucide-react';
import { Language } from '../types/kundali';
import {
  runGoldenKundaliRegression,
  GoldenRegressionSummary
} from '../utils/goldenKundaliSuite';
import { logAuditEvent } from '../lib/auditService';
import { UserProfile } from '../lib/firebase';

interface GoldenKundaliModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Language;
  currentUser: UserProfile | null;
}

export const GoldenKundaliModal: React.FC<GoldenKundaliModalProps> = ({
  isOpen,
  onClose,
  lang,
  currentUser
}) => {
  const [testSummary, setTestSummary] = useState<GoldenRegressionSummary | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  if (!isOpen) return null;

  const isHindi = lang === 'hi';

  const handleRunTests = () => {
    setIsRunning(true);
    setTimeout(() => {
      const summary = runGoldenKundaliRegression();
      setTestSummary(summary);
      setIsRunning(false);

      logAuditEvent(
        'REGRESSION_SUITE_RUN',
        currentUser,
        `Ran Golden Kundali Suite: ${summary.passedTests}/${summary.totalTests} passed in ${summary.totalExecutionTimeMs}ms`
      );
    }, 200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
      <div className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-auto text-astro-text">
        {/* Header */}
        <div className="bg-astro-primary px-5 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-astro-accent" />
            <div>
              <h2 className="text-base sm:text-lg font-bold font-serif">
                {isHindi ? 'गोल्डन कुंडली सत्यापन एवं रिग्रेशन सुइट' : 'Golden Kundali Regression & Validation Suite'}
              </h2>
              <p className="text-[11px] text-white/80">
                {isHindi
                  ? 'मानक संदर्भ तालिकाओं एवं 337 अष्टकवर्ग बिंदुओं के आधार पर गणना शुद्धता का सत्यापन'
                  : 'Calibrated baseline ephemeris, 337 SAV bindus & divisional integrity verification'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 text-xs text-astro-secondary-text space-y-1">
            <p className="font-semibold text-astro-primary">
              Deterministic Calibration Mandate:
            </p>
            <p>
              Before modifying or evolving the platform, this regression runner asserts that all planetary degrees, house systems, Lahiri ayanamsha, and 337 total SAV bindus match exact classical benchmarks.
            </p>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between gap-3">
            <div>
              {testSummary ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-bold text-astro-primary">
                    Status: {testSummary.passedTests} / {testSummary.totalTests} Passed
                  </span>
                  <span className="text-astro-accent">•</span>
                  <span className="text-astro-secondary-text">Executed in {testSummary.totalExecutionTimeMs}ms</span>
                </div>
              ) : (
                <span className="text-xs text-astro-secondary-text">Ready to execute regression verification.</span>
              )}
            </div>

            <button
              onClick={handleRunTests}
              disabled={isRunning}
              className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-2 shadow-xs transition-all disabled:opacity-50"
            >
              {isRunning ? <RotateCcw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 text-astro-accent" />}
              <span>{isHindi ? 'सत्यापन शुरू करें' : 'Run Regression Suite'}</span>
            </button>
          </div>

          {/* Test Results Table */}
          {testSummary && (
            <div className="space-y-3">
              {testSummary.results.map((res) => (
                <div
                  key={res.testId}
                  className={`p-3.5 rounded-xl border transition-all ${
                    res.passed
                      ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950'
                      : 'bg-rose-50 border-rose-200 text-rose-950'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {res.passed ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                      )}
                      <span className="font-bold text-xs">{res.testName}</span>
                    </div>
                    <span className="text-[10px] font-mono text-astro-secondary-text">{res.executionTimeMs} ms</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] pt-1 border-t border-black/5">
                    <div>
                      <span className="text-astro-secondary-text">Lagna:</span>{' '}
                      <span className="font-bold">{res.actualLagna}</span>
                    </div>
                    <div>
                      <span className="text-astro-secondary-text">Moon:</span>{' '}
                      <span className="font-bold">{res.actualMoon}</span>
                    </div>
                    <div>
                      <span className="text-astro-secondary-text">Sun:</span>{' '}
                      <span className="font-bold">{res.actualSun}</span>
                    </div>
                    <div>
                      <span className="text-astro-secondary-text">SAV Total:</span>{' '}
                      <span className="font-bold">{res.actualSAVTotal} / 337</span>
                    </div>
                  </div>

                  {res.errorMessages.length > 0 && (
                    <div className="mt-2 text-[10px] text-rose-700 space-y-0.5">
                      {res.errorMessages.map((e, idx) => (
                        <p key={idx}>• {e}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-astro-card px-5 py-3 border-t border-astro-border flex items-center justify-between text-[11px] text-astro-secondary-text">
          <span>Engine: Vedanga-VedicEngine 1.2 (Lahiri Chitrapaksha)</span>
          <button onClick={onClose} className="font-semibold text-astro-primary hover:underline">
            {isHindi ? 'बंद करें' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};
